<template>
    <div class="legal-preview" :class="conceptClass">
        <div class="concept-switcher" aria-label="เลือกรูปแบบสำหรับพรีวิว">
            <div class="concept-switcher__copy">
                <span class="concept-switcher__label">UX/UI PREVIEW</span>
                <strong>{{ activeConcept }} — {{ currentConcept.name }}</strong>
                <span>{{ currentConcept.description }}</span>
            </div>
            <div class="concept-switcher__options" role="group" aria-label="รูปแบบ A B C">
                <button
                    v-for="concept in concepts"
                    :key="concept.id"
                    type="button"
                    :class="{ active: activeConcept === concept.id }"
                    :aria-pressed="activeConcept === concept.id"
                    @click="selectConcept(concept.id)"
                >
                    {{ concept.id }}
                </button>
            </div>
        </div>

        <header class="legal-hero">
            <div class="legal-shell legal-hero__inner">
                <div class="legal-hero__copy">
                    <div class="product-kicker">
                        <span class="product-mark">M</span>
                        <span>Movevai Messenger</span>
                    </div>
                    <p class="eyebrow">ศูนย์ความเป็นส่วนตัวสำหรับพนักงานขนส่ง</p>
                    <h1>ข้อมูลของคุณ<br><span>ชัดเจน ควบคุมได้</span></h1>
                    <p class="hero-summary">
                        อ่านวิธีที่เราใช้และดูแลข้อมูลส่วนบุคคล หรือส่งคำขอลบบัญชีได้จากหน้าเดียว
                    </p>
                    <div class="hero-actions">
                        <button type="button" class="primary-action" @click="scrollTo('delete-account')">
                            <v-icon size="20">mdi-account-remove-outline</v-icon>
                            ขอลบบัญชี
                        </button>
                        <button type="button" class="secondary-action" @click="scrollTo('privacy-policy')">
                            อ่านนโยบายความเป็นส่วนตัว
                        </button>
                    </div>
                </div>

                <div class="hero-card" aria-label="ข้อมูลสรุป">
                    <div class="hero-card__icon">
                        <v-icon size="34">mdi-shield-check-outline</v-icon>
                    </div>
                    <p class="hero-card__title">สิทธิในข้อมูลเป็นของคุณ</p>
                    <p>ตรวจสอบ แก้ไข ขอสำเนา ถอนความยินยอม หรือขอลบข้อมูลได้ตามสิทธิที่กฎหมายกำหนด</p>
                    <div class="hero-stats">
                        <div><strong>2</strong><span>ช่องทางลบบัญชี</span></div>
                        <div><strong>30</strong><span>วันดำเนินการ</span></div>
                    </div>
                </div>
            </div>
        </header>

        <nav class="section-nav" aria-label="เมนูในหน้า">
            <div class="legal-shell section-nav__inner">
                <button type="button" @click="scrollTo('overview')">ภาพรวม</button>
                <button type="button" @click="scrollTo('privacy-policy')">นโยบายความเป็นส่วนตัว</button>
                <button type="button" @click="scrollTo('delete-account')">คำขอลบบัญชี</button>
                <button type="button" @click="scrollTo('contact')">ติดต่อเรา</button>
            </div>
        </nav>

        <main id="overview" class="legal-shell legal-layout">
            <aside class="legal-aside">
                <p class="aside-label">ในหน้านี้</p>
                <a href="#privacy-policy">นโยบายความเป็นส่วนตัว</a>
                <a href="#delete-account">คำขอลบบัญชี</a>
                <a href="#contact">ช่องทางติดต่อ</a>
                <div class="updated-card">
                    <v-icon size="18">mdi-calendar-blank-outline</v-icon>
                    <div>
                        <span>ปรับปรุงล่าสุด</span>
                        <strong>21 กันยายน 2569</strong>
                    </div>
                </div>
            </aside>

            <div class="legal-content">
                <section class="intro-panel">
                    <div>
                        <span class="section-number">01</span>
                        <h2>เข้าใจข้อมูลของคุณแบบง่าย ๆ</h2>
                    </div>
                    <p>
                        Movevai Co., Ltd. (“Movevai”, “บริษัทฯ”) เคารพความเป็นส่วนตัวของผู้ใช้งานแอปพลิเคชัน
                        Movevai Messenger (“แอป”) หน้านี้อธิบายว่าบริษัทฯ เก็บ ใช้ เปิดเผย และดูแลข้อมูลอย่างไร
                        รวมถึงวิธีส่งคำขอลบบัญชีและข้อมูลส่วนบุคคล
                    </p>
                </section>

                <section id="privacy-policy" class="content-section">
                    <div class="section-heading">
                        <span class="section-number">02</span>
                        <div>
                            <p class="section-kicker">Privacy policy</p>
                            <h2>นโยบายความเป็นส่วนตัว</h2>
                            <p>รายละเอียดข้อมูลที่ใช้เพื่อให้การรับส่งงานปลอดภัยและทำงานได้ครบถ้วน</p>
                        </div>
                    </div>

                    <div class="data-grid">
                        <article v-for="item in collectedData" :key="item.title" class="data-card">
                            <v-icon>{{ item.icon }}</v-icon>
                            <h3>{{ item.title }}</h3>
                            <p>{{ item.text }}</p>
                        </article>
                    </div>

                    <div class="policy-list">
                        <article class="policy-item">
                            <span>01</span>
                            <div>
                                <h3>วัตถุประสงค์ในการใช้ข้อมูล</h3>
                                <p>ใช้เพื่อสมัคร ตรวจสอบ และบริหารบัญชีพนักงานขนส่ง มอบหมายและบริหารงานจัดส่ง แสดงเส้นทางและนำทาง ยืนยันการส่งมอบ ตรวจสอบข้อพิพาท ติดต่อและแจ้งเตือนงาน ป้องกันการใช้งานโดยไม่ได้รับอนุญาต และปฏิบัติตามกฎหมาย</p>
                            </div>
                        </article>
                        <article class="policy-item">
                            <span>02</span>
                            <div>
                                <h3>การใช้ข้อมูลตำแหน่ง</h3>
                                <p>แอปใช้ตำแหน่งเพื่อแสดงตำแหน่ง สนับสนุนการนำทาง ติดตามเส้นทางระหว่างปฏิบัติงาน และยืนยันตำแหน่งเมื่อส่งมอบสินค้า</p>
                                <p>แอปอาจเข้าถึงตำแหน่งขณะที่เปิดใช้งาน และขณะติดตามเส้นทางแม้แอปอยู่เบื้องหลังหรือหน้าจอถูกล็อก ท่านจัดการสิทธิ์ได้ในการตั้งค่าอุปกรณ์ แต่บางฟังก์ชันอาจทำงานไม่สมบูรณ์</p>
                            </div>
                        </article>
                        <article class="policy-item">
                            <span>03</span>
                            <div>
                                <h3>การเปิดเผยข้อมูล</h3>
                                <p>บริษัทฯ อาจเปิดเผยข้อมูลเท่าที่จำเป็นแก่พนักงาน ผู้ดูแลระบบ ผู้ให้บริการที่ได้รับมอบหมาย ผู้ให้บริการโครงสร้างพื้นฐาน ระบบแจ้งเตือน ระบบจัดเก็บข้อมูล แผนที่และการนำทาง รวมถึงหน่วยงานรัฐหรือบุคคลที่มีสิทธิตามกฎหมาย</p>
                                <div class="assurance"><v-icon size="18">mdi-check-decagram-outline</v-icon> บริษัทฯ ไม่ขายข้อมูลส่วนบุคคลให้บุคคลภายนอก</div>
                            </div>
                        </article>
                        <article class="policy-item">
                            <span>04</span>
                            <div>
                                <h3>การเก็บรักษาและความปลอดภัย</h3>
                                <p>บริษัทฯ เก็บข้อมูลเท่าที่จำเป็นตามวัตถุประสงค์หรือระยะเวลาที่กฎหมายกำหนด และใช้มาตรการด้านเทคนิคและการจัดการที่เหมาะสม อย่างไรก็ตาม ไม่มีวิธีส่งหรือจัดเก็บข้อมูลใดปลอดภัยได้อย่างสมบูรณ์</p>
                            </div>
                        </article>
                        <article class="policy-item">
                            <span>05</span>
                            <div>
                                <h3>สิทธิของเจ้าของข้อมูล</h3>
                                <p>ภายใต้กฎหมายที่ใช้บังคับ ท่านอาจมีสิทธิขอเข้าถึง แก้ไข ลบ หรือขอรับสำเนาข้อมูล รวมถึงคัดค้านหรือถอนความยินยอมในการประมวลผลข้อมูลบางกรณี</p>
                            </div>
                        </article>
                        <article class="policy-item">
                            <span>06</span>
                            <div>
                                <h3>การเปลี่ยนแปลงนโยบาย</h3>
                                <p>บริษัทฯ อาจปรับปรุงนโยบายฉบับนี้เป็นครั้งคราว โดยจะแสดงวันที่มีผลบังคับใช้ฉบับล่าสุดไว้บนหน้านี้</p>
                            </div>
                        </article>
                    </div>
                </section>

                <section id="delete-account" class="content-section delete-section">
                    <div class="section-heading">
                        <span class="section-number">03</span>
                        <div>
                            <p class="section-kicker">Account deletion</p>
                            <h2>คำขอลบบัญชี</h2>
                            <p>เลือกวิธีที่สะดวก ระบบจะตรวจสอบตัวตนก่อนดำเนินการเพื่อป้องกันการลบข้อมูลโดยไม่ได้รับอนุญาต</p>
                        </div>
                    </div>

                    <div class="method-grid">
                        <article class="method-card method-card--recommended">
                            <span class="recommended-badge">แนะนำ</span>
                            <div class="method-icon"><v-icon>mdi-cellphone</v-icon></div>
                            <p class="method-number">วิธีที่ 1</p>
                            <h3>ขอผ่านแอป</h3>
                            <p>เปิดแอป Movevai Messenger ไปที่แท็บ <strong>บัญชี</strong> แล้วกด <strong>ขอลบบัญชี</strong> และยืนยัน</p>
                            <div class="method-note">ยกเลิกคำขอได้เอง ตราบใดที่เจ้าหน้าที่ยังไม่เริ่มดำเนินการ</div>
                        </article>
                        <article class="method-card">
                            <div class="method-icon"><v-icon>mdi-email-outline</v-icon></div>
                            <p class="method-number">วิธีที่ 2</p>
                            <h3>ขอผ่านอีเมล</h3>
                            <p>ส่งชื่อ-นามสกุล หมายเลขโทรศัพท์ที่ผูกกับบัญชี และรายละเอียดคำขอ พร้อมระบุหัวข้อ “คำขอลบบัญชี Movevai Messenger”</p>
                            <a class="email-button" href="mailto:movevai.th@gmail.com?subject=คำขอลบบัญชี%20Movevai%20Messenger">
                                movevai.th@gmail.com
                                <v-icon size="18">mdi-arrow-top-right</v-icon>
                            </a>
                        </article>
                    </div>

                    <div class="timeline-panel">
                        <h3>ขั้นตอนหลังส่งคำขอ</h3>
                        <div class="timeline">
                            <div><span>1</span><p><strong>ส่งคำขอ</strong>ผ่านแอปหรืออีเมล</p></div>
                            <div><span>2</span><p><strong>ยืนยันตัวตน</strong>ป้องกันการลบโดยไม่ได้รับอนุญาต</p></div>
                            <div><span>3</span><p><strong>ดำเนินการภายใน 30 วัน</strong>ระหว่างรอยังใช้แอปได้ตามปกติ</p></div>
                        </div>
                    </div>

                    <div class="retention-grid">
                        <article class="retention-card retention-card--delete">
                            <div class="retention-title"><v-icon>mdi-delete-outline</v-icon><h3>ข้อมูลที่จะถูกลบ</h3></div>
                            <ul>
                                <li>ชื่อ-นามสกุล เบอร์โทรศัพท์ และที่อยู่</li>
                                <li>รูปโปรไฟล์ รูปบัตร และเลขบัตรประชาชน</li>
                                <li>ประเภทรถ สีรถ และทะเบียนรถ</li>
                                <li>บัญชี รหัส PIN และอุปกรณ์ที่เคยเข้าสู่ระบบ</li>
                                <li>Token แจ้งเตือน ประวัติการเดินทาง และพิกัด GPS</li>
                            </ul>
                            <p class="retention-footnote">เมื่อลบแล้ว ท่านจะเข้าสู่ระบบไม่ได้อีก</p>
                        </article>
                        <article class="retention-card retention-card--keep">
                            <div class="retention-title"><v-icon>mdi-archive-outline</v-icon><h3>ข้อมูลที่ยังเก็บไว้</h3></div>
                            <ul>
                                <li>รหัสพนักงาน โดยเปลี่ยนชื่อเป็น “ผู้ใช้ที่ลบบัญชีแล้ว”</li>
                                <li>งานที่ปิดแล้ว พร้อมรูปหลักฐาน ลายเซ็นผู้รับ เวลา และพิกัดส่งมอบ</li>
                            </ul>
                            <p class="retention-footnote">เก็บเท่าที่จำเป็นตามกฎหมาย บัญชี ภาษี ป้องกันการทุจริต หรือระงับข้อพิพาท</p>
                        </article>
                    </div>
                </section>

                <section id="contact" class="contact-panel">
                    <div>
                        <p class="section-kicker">Need help?</p>
                        <h2>มีคำถามเกี่ยวกับข้อมูลส่วนบุคคล</h2>
                        <p>ทีม Movevai พร้อมช่วยตรวจสอบคำขอและให้ข้อมูลเพิ่มเติม</p>
                    </div>
                    <div class="contact-actions">
                        <a href="mailto:movevai.th@gmail.com"><v-icon size="19">mdi-email-outline</v-icon> movevai.th@gmail.com</a>
                        <p>66/7-9 ซอยสุขาภิบาล 2 ซอย 31 แขวงดอกไม้ เขตประเวศ กรุงเทพมหานคร 10250</p>
                    </div>
                </section>
            </div>
        </main>
    </div>
</template>

<script>
export default {
    name: 'MessengerLegalPreview',
    data: () => ({
        activeConcept: 'A',
        concepts: [
            { id: 'A', name: 'Friendly clarity', description: 'อบอุ่น เป็นมิตร อ่านง่ายด้วยการ์ดและพื้นที่ว่าง' },
            { id: 'B', name: 'Bold messenger', description: 'คมชัด มั่นใจ เน้นตัวตนของงานขนส่งและการลงมือทำ' },
            { id: 'C', name: 'Calm editorial', description: 'เรียบ สุขุม ให้เนื้อหากฎหมายดูเบาและเป็นระเบียบ' }
        ],
        collectedData: [
            { icon: 'mdi-account-outline', title: 'บัญชีและตัวตน', text: 'ชื่อ เบอร์โทร รหัส PIN เลขและรูปบัตรประชาชน รูปโปรไฟล์' },
            { icon: 'mdi-moped-outline', title: 'ข้อมูลรถและที่อยู่', text: 'ประเภทรถ สีรถ ทะเบียนรถ และที่อยู่สำหรับยืนยันข้อมูล' },
            { icon: 'mdi-crosshairs-gps', title: 'ตำแหน่งและเส้นทาง', text: 'พิกัด GPS เวลา ความแม่นยำ ความเร็ว และทิศทางการเคลื่อนที่' },
            { icon: 'mdi-package-variant-closed', title: 'งานและการส่งมอบ', text: 'งานที่ได้รับ สถานะ รูปหลักฐาน ลายเซ็น OTP และตำแหน่งส่งมอบ' },
            { icon: 'mdi-cellphone-lock', title: 'อุปกรณ์และความปลอดภัย', text: 'รหัสอุปกรณ์ ระบบปฏิบัติการ session และ token แจ้งเตือน' },
            { icon: 'mdi-map-marker-account-outline', title: 'ข้อมูลผู้รับสินค้า', text: 'ชื่อ เบอร์โทร ที่อยู่ และพิกัดปลายทางเท่าที่จำเป็นต่องาน' }
        ]
    }),
    computed: {
        currentConcept() {
            return this.concepts.find((concept) => concept.id === this.activeConcept) || this.concepts[0];
        },
        conceptClass() {
            return `concept-${this.activeConcept.toLowerCase()}`;
        }
    },
    created() {
        this.syncConceptFromRoute();
    },
    watch: {
        '$route.query.concept'() {
            this.syncConceptFromRoute();
        }
    },
    methods: {
        syncConceptFromRoute() {
            const concept = String(this.$route.query.concept || 'a').toUpperCase();
            this.activeConcept = ['A', 'B', 'C'].includes(concept) ? concept : 'A';
        },
        selectConcept(concept) {
            this.activeConcept = concept;
            this.$router.replace({
                query: { ...this.$route.query, concept: concept.toLowerCase() }
            }).catch(() => {});
        },
        scrollTo(id) {
            const element = document.getElementById(id);
            if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
};
</script>

<style scoped>
.legal-preview {
    --ink: #1e201d;
    --muted: #60645d;
    --accent: #e7aa00;
    --accent-soft: #fff1bd;
    --surface: #ffffff;
    --canvas: #f5f3ed;
    --line: #dfded6;
    --radius: 26px;
    color: var(--ink);
    background: var(--canvas);
    font-size: 16px;
    min-height: 100%;
    padding-bottom: 72px;
    transition: background-color 180ms ease, color 180ms ease;
}

.legal-shell {
    margin: 0 auto;
    max-width: 1180px;
    padding-left: 28px;
    padding-right: 28px;
}

.concept-switcher {
    align-items: center;
    background: #171814;
    color: #fff;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    min-height: 74px;
    padding: 12px max(28px, calc((100vw - 1180px) / 2 + 28px));
}

.concept-switcher__copy {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
}

.concept-switcher__copy strong { font-size: 0.96rem; }
.concept-switcher__copy > span:last-child { color: #bfc1ba; font-size: 0.82rem; }
.concept-switcher__label { color: #eebd22; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.13em; }

.concept-switcher__options {
    background: #292a25;
    border: 1px solid #3c3d36;
    border-radius: 999px;
    display: flex;
    flex: 0 0 auto;
    gap: 4px;
    padding: 4px;
}

.concept-switcher__options button {
    border-radius: 999px;
    color: #c8c9c4;
    font-weight: 700;
    height: 36px;
    transition: all 160ms ease;
    width: 42px;
}

.concept-switcher__options button.active { background: #efb818; color: #171814; }

.legal-hero {
    background: var(--accent-soft);
    overflow: hidden;
    position: relative;
}

.legal-hero::after {
    border: 1px solid rgba(30, 32, 29, 0.12);
    border-radius: 50%;
    content: '';
    height: 460px;
    position: absolute;
    right: -130px;
    top: -210px;
    width: 460px;
}

.legal-hero__inner {
    align-items: center;
    display: grid;
    gap: 64px;
    grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.6fr);
    min-height: 535px;
    padding-bottom: 68px;
    padding-top: 68px;
    position: relative;
    z-index: 1;
}

.product-kicker { align-items: center; display: flex; font-size: 0.82rem; font-weight: 600; gap: 9px; margin-bottom: 34px; }
.product-mark { align-items: center; background: var(--ink); border-radius: 9px; color: var(--accent-soft); display: inline-flex; font-size: 0.72rem; height: 28px; justify-content: center; width: 28px; }
.eyebrow, .section-kicker { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.13em; margin-bottom: 12px; text-transform: uppercase; }

.legal-hero h1 {
    font-size: clamp(2.7rem, 5vw, 4.7rem);
    font-weight: 700;
    letter-spacing: -0.045em;
    line-height: 1.08;
    margin-bottom: 24px;
}

.legal-hero h1 span { color: #936900; }
.hero-summary { font-size: 1.12rem; line-height: 1.8; margin-bottom: 30px; max-width: 650px; }
.hero-actions { display: flex; flex-wrap: wrap; gap: 12px; }
.hero-actions button { align-items: center; border-radius: 999px; display: inline-flex; font-weight: 600; gap: 9px; min-height: 50px; padding: 12px 22px; }
.primary-action { background: var(--ink); color: #fff; }
.primary-action .v-icon { color: #fff; }
.secondary-action { border: 1px solid rgba(30, 32, 29, 0.3); }

.hero-card {
    background: rgba(255, 255, 255, 0.76);
    border: 1px solid rgba(30, 32, 29, 0.12);
    border-radius: var(--radius);
    box-shadow: 0 24px 60px rgba(84, 62, 0, 0.10);
    padding: 32px;
}

.hero-card__icon { align-items: center; background: var(--ink); border-radius: 50%; display: flex; height: 62px; justify-content: center; margin-bottom: 28px; width: 62px; }
.hero-card__icon .v-icon { color: var(--accent-soft); }
.hero-card__title { font-size: 1.25rem; font-weight: 700; margin-bottom: 8px; }
.hero-card > p:not(.hero-card__title) { color: var(--muted); line-height: 1.7; }
.hero-stats { border-top: 1px solid var(--line); display: grid; gap: 16px; grid-template-columns: 1fr 1fr; margin-top: 24px; padding-top: 22px; }
.hero-stats div { display: flex; flex-direction: column; }
.hero-stats strong { font-size: 1.8rem; line-height: 1; }
.hero-stats span { color: var(--muted); font-size: 0.75rem; margin-top: 7px; }

.section-nav { background: var(--surface); border-bottom: 1px solid var(--line); border-top: 1px solid var(--line); position: sticky; top: 64px; z-index: 4; }
.section-nav__inner { display: flex; gap: 34px; overflow-x: auto; }
.section-nav button { border-bottom: 3px solid transparent; color: var(--muted); flex: 0 0 auto; font-size: 0.9rem; min-height: 58px; }
.section-nav button:hover, .section-nav button:focus { border-bottom-color: var(--accent); color: var(--ink); }

.legal-layout { display: grid; gap: 64px; grid-template-columns: 210px minmax(0, 1fr); padding-top: 72px; }
.legal-aside { align-self: start; display: flex; flex-direction: column; position: sticky; top: 148px; }
.aside-label { color: var(--muted); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; margin-bottom: 16px; text-transform: uppercase; }
.legal-aside > a { border-left: 2px solid var(--line); color: var(--muted); font-size: 0.86rem; line-height: 1.4; padding: 9px 0 9px 16px; text-decoration: none; }
.legal-aside > a:hover { border-left-color: var(--accent); color: var(--ink); }
.updated-card { align-items: flex-start; background: var(--surface); border: 1px solid var(--line); border-radius: 14px; display: flex; gap: 10px; margin-top: 28px; padding: 14px; }
.updated-card div { display: flex; flex-direction: column; }
.updated-card span { color: var(--muted); font-size: 0.68rem; }
.updated-card strong { font-size: 0.76rem; margin-top: 2px; }

.legal-content { min-width: 0; }
.intro-panel { align-items: start; background: var(--ink); border-radius: var(--radius); color: #fff; display: grid; gap: 40px; grid-template-columns: 0.8fr 1.2fr; margin-bottom: 80px; padding: 40px; }
.section-number { color: var(--accent); display: block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; margin-bottom: 13px; }
.intro-panel h2, .section-heading h2, .contact-panel h2 { font-size: clamp(1.7rem, 3vw, 2.45rem); letter-spacing: -0.025em; line-height: 1.25; }
.intro-panel p { color: #d3d4ce; line-height: 1.85; margin: 0; }
.content-section { scroll-margin-top: 150px; margin-bottom: 94px; }
.section-heading { align-items: start; display: grid; gap: 22px; grid-template-columns: 52px 1fr; margin-bottom: 34px; }
.section-heading .section-number { border-top: 2px solid var(--accent); padding-top: 10px; }
.section-heading h2 { margin-bottom: 9px; }
.section-heading p:last-child { color: var(--muted); line-height: 1.7; margin: 0; max-width: 700px; }

.data-grid { display: grid; gap: 14px; grid-template-columns: repeat(3, 1fr); margin-bottom: 50px; }
.data-card { background: var(--surface); border: 1px solid var(--line); border-radius: calc(var(--radius) * 0.7); padding: 22px; }
.data-card .v-icon { color: var(--accent); margin-bottom: 30px; }
.data-card h3 { font-size: 0.98rem; margin-bottom: 8px; }
.data-card p { color: var(--muted); font-size: 0.84rem; line-height: 1.65; margin: 0; }

.policy-list { border-top: 1px solid var(--line); }
.policy-item { border-bottom: 1px solid var(--line); display: grid; gap: 28px; grid-template-columns: 44px 1fr; padding: 30px 0; }
.policy-item > span { color: var(--accent); font-size: 0.75rem; font-weight: 700; }
.policy-item h3 { font-size: 1.1rem; margin-bottom: 10px; }
.policy-item p { color: var(--muted); line-height: 1.8; margin-bottom: 10px; }
.assurance { align-items: center; background: #eaf6ec; border-radius: 12px; color: #24552c; display: inline-flex; font-size: 0.82rem; gap: 8px; padding: 9px 12px; }
.assurance .v-icon { color: #2f7b3b; }

.method-grid, .retention-grid { display: grid; gap: 18px; grid-template-columns: 1fr 1fr; }
.method-card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); min-height: 315px; padding: 30px; position: relative; }
.method-card--recommended { border: 2px solid var(--accent); }
.recommended-badge { background: var(--accent); border-radius: 999px; font-size: 0.7rem; font-weight: 700; padding: 6px 11px; position: absolute; right: 20px; top: 20px; }
.method-icon { align-items: center; background: var(--accent-soft); border-radius: 14px; display: flex; height: 52px; justify-content: center; margin-bottom: 26px; width: 52px; }
.method-number { color: var(--muted); font-size: 0.75rem; margin-bottom: 2px; }
.method-card h3 { font-size: 1.25rem; margin-bottom: 12px; }
.method-card > p:not(.method-number) { color: var(--muted); font-size: 0.9rem; line-height: 1.7; }
.method-note { border-top: 1px solid var(--line); color: var(--muted); font-size: 0.78rem; margin-top: 24px; padding-top: 16px; }
.email-button { align-items: center; color: var(--ink); display: inline-flex; font-size: 0.86rem; font-weight: 600; gap: 6px; margin-top: 8px; text-decoration: none; }

.timeline-panel { background: var(--ink); border-radius: var(--radius); color: #fff; margin: 20px 0; padding: 32px; }
.timeline-panel > h3 { margin-bottom: 26px; }
.timeline { display: grid; gap: 28px; grid-template-columns: repeat(3, 1fr); }
.timeline > div { display: grid; gap: 12px; grid-template-columns: 28px 1fr; }
.timeline span { align-items: center; background: var(--accent); border-radius: 50%; color: var(--ink); display: flex; font-size: 0.75rem; font-weight: 700; height: 28px; justify-content: center; }
.timeline p { color: #c8cac4; font-size: 0.78rem; line-height: 1.6; margin: 0; }
.timeline strong { color: #fff; display: block; font-size: 0.86rem; }

.retention-card { border: 1px solid var(--line); border-radius: var(--radius); padding: 28px; }
.retention-card--delete { background: #fff5f1; }
.retention-card--keep { background: #f0f4f8; }
.retention-title { align-items: center; display: flex; gap: 10px; margin-bottom: 18px; }
.retention-title h3 { font-size: 1.05rem; }
.retention-card ul { color: var(--muted); font-size: 0.83rem; line-height: 1.75; padding-left: 19px; }
.retention-footnote { border-top: 1px solid rgba(30, 32, 29, 0.12); color: var(--muted); font-size: 0.75rem; line-height: 1.6; margin: 18px 0 0; padding-top: 14px; }

.contact-panel { align-items: center; background: var(--accent-soft); border-radius: var(--radius); display: grid; gap: 40px; grid-template-columns: 1.1fr 0.9fr; padding: 40px; scroll-margin-top: 150px; }
.contact-panel > div:first-child > p:last-child { color: var(--muted); line-height: 1.7; margin: 10px 0 0; }
.contact-actions a { align-items: center; color: var(--ink); display: inline-flex; font-weight: 700; gap: 8px; margin-bottom: 14px; }
.contact-actions p { color: var(--muted); font-size: 0.78rem; line-height: 1.6; margin: 0; }

/* B — bold, operational and high contrast */
.concept-b { --accent: #ffc400; --accent-soft: #ffc400; --canvas: #eceee8; --line: #cfd2ca; --radius: 5px; }
.concept-b .legal-hero { background: #20231e; color: #fff; }
.concept-b .legal-hero::after { border: 110px solid rgba(255, 196, 0, 0.06); height: 520px; right: -90px; top: -150px; width: 520px; }
.concept-b .product-mark { background: var(--accent); color: #151713; }
.concept-b .legal-hero h1 { font-weight: 700; text-transform: uppercase; }
.concept-b .legal-hero h1 span { color: var(--accent); }
.concept-b .hero-summary { color: #cfd2ca; }
.concept-b .primary-action { background: var(--accent); color: #161813; }
.concept-b .primary-action .v-icon { color: #161813; }
.concept-b .secondary-action { border-color: #666a61; color: #fff; }
.concept-b .hero-card { background: #2b2f28; border-color: #484d43; box-shadow: 12px 12px 0 var(--accent); }
.concept-b .hero-card__icon { background: var(--accent); }
.concept-b .hero-card__icon .v-icon { color: #1b1e19; }
.concept-b .hero-card > p:not(.hero-card__title), .concept-b .hero-stats span { color: #bfc3ba; }
.concept-b .hero-stats { border-color: #4b5046; }
.concept-b .section-nav button:hover, .concept-b .section-nav button:focus { background: var(--accent); border-color: var(--accent); color: #161813; }
.concept-b .data-card { border-width: 0 0 4px; }
.concept-b .data-card .v-icon { background: #20231e; color: var(--accent); padding: 8px; }
.concept-b .method-card--recommended { box-shadow: 8px 8px 0 var(--accent); }
.concept-b .contact-panel { border: 2px solid #1e201d; }

/* C — quiet, editorial and minimal */
.concept-c { --accent: #8d6c17; --accent-soft: #f2eee2; --canvas: #fbfbf8; --line: #deded8; --radius: 0px; }
.concept-c .legal-hero { background: #fbfbf8; border-bottom: 1px solid var(--line); }
.concept-c .legal-hero::after { border-color: rgba(141, 108, 23, 0.18); height: 330px; right: 2vw; top: 70px; width: 330px; }
.concept-c .legal-hero h1 { font-weight: 500; letter-spacing: -0.035em; }
.concept-c .legal-hero h1 span { color: var(--ink); font-style: italic; }
.concept-c .product-mark { background: transparent; border: 1px solid var(--ink); color: var(--ink); }
.concept-c .hero-card { border-width: 0 0 0 1px; box-shadow: none; padding-left: 40px; }
.concept-c .hero-card__icon { background: transparent; border: 1px solid var(--line); }
.concept-c .hero-card__icon .v-icon { color: var(--accent); }
.concept-c .section-nav { background: rgba(251, 251, 248, 0.96); }
.concept-c .intro-panel { background: transparent; border-bottom: 1px solid var(--ink); border-top: 1px solid var(--ink); color: var(--ink); padding-left: 0; padding-right: 0; }
.concept-c .intro-panel p { color: var(--muted); }
.concept-c .data-grid { gap: 0; }
.concept-c .data-card { border-width: 1px 0 0; }
.concept-c .data-card:nth-last-child(-n+3) { border-bottom-width: 1px; }
.concept-c .method-card { border-width: 1px 0; }
.concept-c .method-card--recommended { border-color: var(--ink); border-width: 1px 0; }
.concept-c .timeline-panel { background: var(--accent-soft); color: var(--ink); }
.concept-c .timeline p { color: var(--muted); }
.concept-c .timeline strong { color: var(--ink); }
.concept-c .contact-panel { border-bottom: 1px solid var(--ink); border-top: 1px solid var(--ink); padding-left: 0; padding-right: 0; }

@media (max-width: 959px) {
    .legal-hero__inner { gap: 34px; grid-template-columns: 1fr; }
    .hero-card { max-width: 560px; }
    .legal-layout { gap: 34px; grid-template-columns: 1fr; }
    .legal-aside { display: none; }
    .section-nav { top: 56px; }
    .content-section, .contact-panel { scroll-margin-top: 130px; }
}

@media (max-width: 700px) {
    .legal-preview { padding-bottom: 40px; }
    .legal-shell { padding-left: 18px; padding-right: 18px; }
    .concept-switcher { align-items: flex-start; padding: 12px 18px; }
    .concept-switcher__copy > span:last-child { display: none; }
    .concept-switcher__options button { height: 32px; width: 35px; }
    .legal-hero__inner { min-height: 0; padding-bottom: 46px; padding-top: 42px; }
    .product-kicker { margin-bottom: 28px; }
    .legal-hero h1 { font-size: 2.65rem; }
    .hero-summary { font-size: 1rem; }
    .hero-actions { align-items: stretch; flex-direction: column; }
    .hero-actions button { justify-content: center; width: 100%; }
    .hero-card { padding: 24px; }
    .section-nav__inner { gap: 24px; }
    .section-nav button { font-size: 0.8rem; }
    .legal-layout { padding-top: 42px; }
    .intro-panel, .contact-panel { gap: 22px; grid-template-columns: 1fr; margin-bottom: 62px; padding: 28px 22px; }
    .section-heading { gap: 14px; grid-template-columns: 36px 1fr; }
    .content-section { margin-bottom: 68px; }
    .data-grid { grid-template-columns: 1fr 1fr; }
    .data-card { padding: 18px; }
    .data-card .v-icon { margin-bottom: 22px; }
    .method-grid, .retention-grid { grid-template-columns: 1fr; }
    .timeline { grid-template-columns: 1fr; }
    .concept-c .hero-card { border-left: 0; border-top: 1px solid var(--line); padding-left: 0; }
    .concept-c .data-card:nth-last-child(-n+3) { border-bottom-width: 0; }
    .concept-c .data-card:last-child { border-bottom-width: 1px; }
}

@media (max-width: 430px) {
    .concept-switcher__copy { display: block; }
    .concept-switcher__copy strong { display: block; margin-top: 2px; }
    .legal-hero h1 { font-size: 2.25rem; }
    .data-grid { grid-template-columns: 1fr; }
    .policy-item { gap: 12px; grid-template-columns: 28px 1fr; }
    .method-card, .retention-card { padding: 22px; }
}
</style>
